import { useState } from 'react'
import { FileText, LoaderCircle, Printer } from 'lucide-react'
import type { StoryProject } from '../types'
import {
  downloadReviewDocx,
  openPrintReview,
  reviewParagraphs,
} from '../reviewExport'
import { WorkspaceDialog } from './WorkspaceDialog'

export function ReviewExport({
  project,
  selectedNodeId,
  onClose,
}: {
  project: StoryProject
  selectedNodeId: string
  onClose: () => void
}) {
  const [sceneOnly, setSceneOnly] = useState(false)
  const [includeLogic, setIncludeLogic] = useState(true)
  const [newPagePerScene, setNewPagePerScene] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const options = {
    nodeIds: sceneOnly ? [selectedNodeId] : undefined,
    includeLogic,
    newPagePerScene,
  }
  const preview = reviewParagraphs(project, {
    ...options,
    nodeIds: [sceneOnly ? selectedNodeId : project.nodes[0].id],
  })
  return (
    <WorkspaceDialog
      title="Share a review copy"
      eyebrow="A MANUSCRIPT THAT TRAVELS WELL"
      onClose={onClose}
      className="review-export-dialog"
    >
      <div className="review-export-body">
        <p>
          Send an editable Word document, or open a clean print view to save as
          PDF. Dialogue cues, directions, and route destinations stay with the
          manuscript.
        </p>
        <div className="find-options">
          <label>
            <input
              type="checkbox"
              checked={sceneOnly}
              onChange={(event) => setSceneOnly(event.target.checked)}
            />{' '}
            Current scene only
          </label>
          <label>
            <input
              type="checkbox"
              checked={includeLogic}
              onChange={(event) => setIncludeLogic(event.target.checked)}
            />{' '}
            Include story rules
          </label>
          <label>
            <input
              type="checkbox"
              checked={newPagePerScene}
              onChange={(event) => setNewPagePerScene(event.target.checked)}
            />{' '}
            New page per scene
          </label>
        </div>
        <div className="review-preview-label">
          Preview of {sceneOnly ? 'the current' : 'the first'} scene{' '}
          <span>
            {sceneOnly ? 1 : project.nodes.length}{' '}
            {sceneOnly || project.nodes.length === 1 ? 'scene' : 'scenes'} in
            your export
          </span>
        </div>
        <article
          className="review-copy-preview"
          aria-label="Review copy preview"
        >
          {preview.map((paragraph, index) =>
            paragraph.role === 'title' ? (
              <h3 key={index}>{paragraph.text}</h3>
            ) : (
              <p key={index} className={'review-paragraph--' + paragraph.role}>
                {paragraph.text}
              </p>
            ),
          )}
        </article>
        <p className="review-private-note">
          Private scene notes and story-bible profiles stay in your project
          backup.
        </p>
        {error && (
          <p className="dialog-message dialog-message--error" role="alert">
            {error}
          </p>
        )}
        {notice && (
          <p className="dialog-message" role="status">
            {notice}
          </p>
        )}
      </div>
      <footer className="workspace-dialog-actions">
        <button
          type="button"
          className="button"
          disabled={busy}
          onClick={() => {
            setError('')
            try {
              openPrintReview(project, options)
              setNotice('Print preview opened in a new tab.')
            } catch (error) {
              setError(
                error instanceof Error
                  ? error.message
                  : 'Print preview could not be opened.',
              )
            }
          }}
        >
          <Printer size={15} /> Print / PDF
        </button>
        <button
          type="button"
          className="button button--primary"
          disabled={busy}
          onClick={async () => {
            setBusy(true)
            setError('')
            try {
              await downloadReviewDocx(project, options)
              setNotice('Your Word review copy is ready to download.')
            } catch {
              setError(
                'The Word export could not be prepared. Reconnect, save your draft, and reload the page to try again.',
              )
            } finally {
              setBusy(false)
            }
          }}
        >
          {busy ? (
            <LoaderCircle size={15} className="spin" />
          ) : (
            <FileText size={15} />
          )}
          {busy ? 'Preparing…' : 'Download Word'}
        </button>
      </footer>
    </WorkspaceDialog>
  )
}
